import { apiClient } from "@/services/apiClient";

/**
 * Registering a profession (spec 8a948f97).
 *
 * The registry stopped being something only a migration could write. Every
 * rule the backend enforces here guards a failure that is SILENT — a missing
 * fee rate earns 0% with no error, `rfq_and_catalog` admits a profession to
 * two frozen catalog clones, a 17-character slug dies halfway through the
 * insert — so this module deliberately mirrors none of them client-side except
 * where a form field needs the shape. The server refuses; the form explains.
 */

export interface ProfessionCredential {
  prfId: number;
  attributeKind: string | null;
  attributeValue: string | null;
}

export interface ProfessionAdminRow {
  slug: string;
  displayName: string;
  nounLabel: string | null;
  nounInSentence: string | null;
  consumerHint: string | null;
  scopeExamples: string | null;
  iconKey: string | null;
  /** Every verification this profession requires. ALL of them must pass. */
  verificationStrategies: string[];
  registerTypeCode: string | null;
  deliveryModel: string;
  commissionPermitted: boolean;
  isActive: boolean;
  triageEligible: boolean;
  sortOrder: number;
  /**
   * NULL means no fee-rate row exists, which is NOT the same as 0%.
   * Nothing created through this surface can be in that state; a profession
   * registered before it could.
   */
  consumerFeePercent: number | null;
  credentials: ProfessionCredential[];
}

export interface CreateProfessionBody {
  slug: string;
  displayName: string;
  nounLabel: string;
  nounInSentence: string;
  consumerHint?: string | null;
  scopeExamples?: string | null;
  iconKey?: string | null;
  verificationStrategies: string[];
  registerTypeCode?: string | null;
  credentials?: Array<{ prfId: number; attributeKind?: string | null; attributeValue?: string | null }>;
  sortOrder?: number;
  isActive?: boolean;
  triageEligible?: boolean;
}

/**
 * No `slug` and no `deliveryModel`, matching the backend DTO.
 *
 * Not an oversight on either side: the slug is a primary key behind four
 * foreign keys and embedded in live invitation URLs, and the global pipe
 * strips anything the DTO does not declare. There is no delete for the same
 * reason — `isActive` is how a profession stops being offered.
 */
export type UpdateProfessionBody = Partial<
  Pick<
    CreateProfessionBody,
    | "displayName"
    | "nounLabel"
    | "nounInSentence"
    | "consumerHint"
    | "scopeExamples"
    | "iconKey"
    | "verificationStrategies"
    | "registerTypeCode"
    | "sortOrder"
    | "isActive"
    | "triageEligible"
  >
>;

export function listProfessionsForAdmin(): Promise<ProfessionAdminRow[]> {
  return apiClient
    .get<ProfessionAdminRow[]>("/platform/professions/admin")
    .then((res) => res.data);
}

/**
 * The strategies the backend actually implements.
 *
 * FETCHED, never hardcoded. A literal list here would drift the moment a
 * fourth strategy shipped and — worse — could offer one that does not exist,
 * producing a profession whose first applicant throws at verification. This is
 * also why `manual_review` does not appear despite being the column's default:
 * nothing implements it.
 */
export function listVerificationStrategies(): Promise<string[]> {
  return apiClient
    .get<{ strategies: string[] }>("/platform/professions/verification-strategies")
    .then((res) => res.data.strategies);
}

export function createProfession(body: CreateProfessionBody): Promise<ProfessionAdminRow> {
  return apiClient.post<ProfessionAdminRow>("/platform/professions", body).then((res) => res.data);
}

export function updateProfession(
  slug: string,
  body: UpdateProfessionBody,
): Promise<ProfessionAdminRow> {
  return apiClient
    .patch<ProfessionAdminRow>(`/platform/professions/${encodeURIComponent(slug)}`, body)
    .then((res) => res.data);
}
